import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Shield, FileText, DollarSign, Cookie, FileCheck,
  Save, Loader2, ChevronDown, ChevronRight, ArrowLeft,
  Eye, Calendar, Pencil, CheckCircle, AlertCircle, ExternalLink
} from "lucide-react";

const PAGE_ICONS = {
  "privacy-policy": Shield,
  "terms-of-service": FileText,
  "refund-policy": DollarSign,
  "cookie-policy": Cookie,
  "acceptable-use": FileCheck
};

const PAGE_COLORS = {
  "privacy-policy": "from-blue-600 to-purple-600",
  "terms-of-service": "from-purple-600 to-pink-600",
  "refund-policy": "from-green-600 to-teal-600",
  "cookie-policy": "from-orange-500 to-amber-600",
  "acceptable-use": "from-red-600 to-rose-600"
};

const PAGE_ROUTES = {
  "privacy-policy": "/privacy",
  "terms-of-service": "/terms",
  "refund-policy": "/refund",
  "cookie-policy": "/cookies",
  "acceptable-use": "/acceptable-use"
};

const AdminLegalCompliance = () => {
  const { token } = useContext(AuthContext);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/legal-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data.pages || []);
    } catch (err) {
      toast.error("Failed to load legal pages");
    } finally {
      setLoading(false);
    }
  };

  const selectPage = (page) => {
    setSelectedPage(page);
    setEditData(JSON.parse(JSON.stringify(page)));
    setExpandedSections({});
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const updatePageField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const updateSection = (sectionIndex, field, value) => {
    setEditData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value };
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setEditData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        { id: `section-${Date.now()}`, title: "New Section", icon: "FileText", content: "" }
      ]
    }));
  };

  const removeSection = (sectionIndex) => {
    setEditData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== sectionIndex)
    }));
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    setEditData(prev => {
      const sections = [...prev.sections];
      [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
      return { ...prev, sections };
    });
  };

  const moveSectionDown = (index) => {
    setEditData(prev => {
      if (index >= prev.sections.length - 1) return prev;
      const sections = [...prev.sections];
      [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
      return { ...prev, sections };
    });
  };

  const savePage = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/legal-pages/${editData.slug}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${editData.title} saved successfully`);
      await fetchPages();
      const updated = (await axios.get(`${API}/legal-pages/${editData.slug}`)).data;
      setSelectedPage(updated);
      setEditData(JSON.parse(JSON.stringify(updated)));
    } catch (err) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Page list view
  if (!selectedPage) {
    return (
      <div className="space-y-6" data-testid="admin-legal-compliance">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Legal & Compliance Pages</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage the content of all legal and compliance pages
            </p>
          </div>
          <Badge variant="secondary" className="text-sm">
            {pages.length} Pages
          </Badge>
        </div>

        <div className="grid gap-4">
          {pages.map((page) => {
            const IconComp = PAGE_ICONS[page.slug] || FileText;
            const gradient = PAGE_COLORS[page.slug] || "from-gray-600 to-gray-800";
            const route = PAGE_ROUTES[page.slug] || "#";
            return (
              <Card
                key={page.slug}
                className="hover:shadow-lg transition-shadow cursor-pointer border-0 shadow-md"
                data-testid={`legal-page-card-${page.slug}`}
              >
                <CardContent className="p-0">
                  <div className="flex items-center gap-0">
                    <div className={`w-20 h-20 bg-gradient-to-br ${gradient} flex items-center justify-center rounded-l-lg shrink-0`}>
                      <IconComp className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 p-4 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900 truncate">{page.title}</h4>
                        {page.updated_at && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                            Customized
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{page.subtitle}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {page.last_updated}
                        </span>
                        <span>{page.sections?.length || 0} sections</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-4 shrink-0">
                      <a
                        href={route}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
                        title="Preview page"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                      <Button
                        size="sm"
                        onClick={() => selectPage(page)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                        data-testid={`edit-${page.slug}-btn`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Section editor view
  const IconComp = PAGE_ICONS[editData.slug] || FileText;
  const gradient = PAGE_COLORS[editData.slug] || "from-gray-600 to-gray-800";

  return (
    <div className="space-y-6" data-testid="admin-legal-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSelectedPage(null); setEditData(null); }}
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
            <IconComp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Editing: {editData.title}</h3>
            <p className="text-xs text-gray-500">{editData.sections?.length} sections</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={PAGE_ROUTES[editData.slug]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Preview
          </a>
          <Button
            onClick={savePage}
            disabled={saving}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white"
            data-testid="save-legal-page-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Page Meta */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Page Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Page Title</Label>
              <Input
                value={editData.title}
                onChange={(e) => updatePageField("title", e.target.value)}
                data-testid="legal-page-title-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Subtitle</Label>
              <Input
                value={editData.subtitle}
                onChange={(e) => updatePageField("subtitle", e.target.value)}
                data-testid="legal-page-subtitle-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Last Updated Date</Label>
            <Input
              value={editData.last_updated}
              onChange={(e) => updatePageField("last_updated", e.target.value)}
              placeholder="e.g., January 30, 2026"
              data-testid="legal-page-date-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-800">Sections</h4>
          <Button variant="outline" size="sm" onClick={addSection} data-testid="add-section-btn">
            + Add Section
          </Button>
        </div>

        {editData.sections?.map((section, index) => {
          const isExpanded = expandedSections[section.id];
          return (
            <Card key={section.id || index} className="border shadow-sm">
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => toggleSection(section.id)}
                data-testid={`section-toggle-${index}`}
              >
                {isExpanded
                  ? <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                  : <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                }
                <span className="font-medium text-gray-900 flex-1">{section.title}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => { e.stopPropagation(); moveSectionUp(index); }}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => { e.stopPropagation(); moveSectionDown(index); }}
                    disabled={index === editData.sections.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); removeSection(index); }}
                  >
                    ×
                  </Button>
                </div>
              </div>

              {isExpanded && (
                <CardContent className="pt-0 pb-4 px-4 space-y-3 border-t">
                  <div>
                    <Label className="text-sm font-medium">Section Title</Label>
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(index, "title", e.target.value)}
                      data-testid={`section-title-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      Content
                      <span className="text-xs text-gray-400 ml-2">
                        (Use **bold**, - for bullets, \n for new lines)
                      </span>
                    </Label>
                    <Textarea
                      value={section.content}
                      onChange={(e) => updateSection(index, "content", e.target.value)}
                      rows={8}
                      className="font-mono text-sm"
                      data-testid={`section-content-${index}`}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Bottom save */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={savePage}
          disabled={saving}
          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminLegalCompliance;
